// Ported from dev-scrips-pm-react src/patients/services/use-emirates-id-agent-scan.ts (PROD-1716).
/**
 * PROD-1289 (PM) / PROD-1716 (PA) — pull-based Emirates ID scan via the local headless agent
 * (Option 1).
 *
 * Drop-in sibling of the passive SignalR hook `useEmiratesIdScanner`: same raw
 * `EmiratesIdCardData` payload the SignalR hub delivers, but triggered on demand
 * (a "Read Emirates ID" button) instead of a server push. Wiring in the
 * create/edit patient form:
 *
 *   const { readCard, isReading } = useEmiratesIdAgentScan({ onScan: handleScan });
 *   // <Button onClick={readCard} disabled={isReading}>Read Emirates ID</Button>
 *   // Keep useEmiratesIdScanner (the passive SignalR variant) mounted alongside —
 *   // this is an on-demand ADDITION, not a replacement.
 *
 * Verification of the button render + live read is deferred to the running app on the
 * reception PC with the reader attached — the reader + capability flow are unit-covered
 * in `read-emirates-id-via-agent.ts`.
 */
import { useCallback, useState } from 'react';
import { useAuthStore } from '@/auth/auth-store';
import {
  EidAgentError,
  readEmiratesIdViaAgent,
} from '@/patients/services/read-emirates-id-via-agent';
import type { EmiratesIdCardData } from '@/shared/types/emirates-id';

export interface UseEmiratesIdAgentScanOptions {
  /** Same raw-payload shape as `useEmiratesIdScanner`'s SignalR handler — receives the
   *  card data to parse + apply. */
  onScan: (data: EmiratesIdCardData) => void;
}

export interface EmiratesIdAgentScanResult {
  /** Trigger a read: mint capability → call the local agent → `onScan(data)` on success. */
  readCard: () => Promise<void>;
  isReading: boolean;
  /** Last failure, typed so the UI can show "insert card" vs "reader agent not running", etc. */
  error: EidAgentError | null;
}

export function useEmiratesIdAgentScan({
  onScan,
}: UseEmiratesIdAgentScanOptions): EmiratesIdAgentScanResult {
  const [isReading, setIsReading] = useState(false);
  const [error, setError] = useState<EidAgentError | null>(null);

  const readCard = useCallback(async () => {
    setError(null);
    setIsReading(true);
    try {
      const token = await useAuthStore.getState().getValidAccessToken();
      const data = await readEmiratesIdViaAgent(token);
      onScan(data);
    } catch (e) {
      setError(e instanceof EidAgentError ? e : new EidAgentError('read_failed', String(e)));
    } finally {
      setIsReading(false);
    }
  }, [onScan]);

  return { readCard, isReading, error };
}
